import { PartnerDetailsType, PartnerRecords } from '~/campaigns/types/campaign.types'
import CoxLogo from '~/components/Icons/svgs/CoxLogo'
import appConfig from '~/config/app'
import { AppLanguage } from '~/types/locale'

export const enum OFFER_TYPE {
  PROCENTAGE_OFF = 'percentage_off',
  AMOUNT_OFF = 'amount_off',
}

// Standalone export, NOT a key in PartnerDetails: Accenture has its own static page, and getStaticPaths in
// pages/campaigns/[...slug].tsx would emit a colliding /campaigns/accenture — a fatal build error.
export const ACCENTURE_PARTNER_DETAILS: PartnerDetailsType = {
  name: 'Accenture',
  couponCode: appConfig.isProdEnvironment
    ? { monthly: 'LEizIUS2', yearly: 'LEizIUS2' }
    : { monthly: 'jDEiQ8Qg', yearly: 'jDEiQ8Qg' },
  offerType: OFFER_TYPE.PROCENTAGE_OFF,
  procentageOff: { monthly: 20, yearly: 20 },
  language: AppLanguage.NO,
}

export const PartnerDetails: PartnerRecords = {
  cox: {
    name: 'Cox Enterprises',
    showOfferBadge: true,
    examOfferYearly: true,
    landingHeroNoteKey: 'customText.coxHeroNote',
    // Yearly: the exam campaign price ($115.20 + $96) with 4.51% off once, so the first year matches $110.
    couponCode: { monthly: 'iKOwlkPo', yearly: 'Cpi208Rw' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 4.51 },
    discountDuration: { yearly: 'firstYear' },
    customPriceData: {
      yearlyPriceId: appConfig.isProdEnvironment ? 'price_1UWCVPJhgnSRU9kupr8nhvbI' : 'price_1UWCZmJhgnSRU9kurOP42yxa',
    },
    language: AppLanguage.EN,
    logo: {
      logoType: 'svgIcon',
      svgIcon: CoxLogo,
      logoSize: 150,
      heroClassName: 'h-7 w-17 md:h-13 md:w-32 text-white',
    },
  },
  appalachian: {
    name: 'Appalachian Wireless',
    showOfferBadge: true,
    couponCode: { monthly: '45ktiI8n', yearly: '45ktiI8n' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/appalachian-white.png',
      logoHeight: 80,
      logoWidth: 300,
    },
  },
  acom: {
    name: 'Alaska Communications',
    showOfferBadge: true,
    couponCode: { monthly: 'j9QLcf4T', yearly: 'j9QLcf4T' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 25, yearly: 25 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/acomLogoWhite.png',
      logoHeight: 100,
      logoWidth: 180,
    },
  },
  peachjar: {
    name: 'Peach Jar',
    showOfferBadge: true,
    // Same coupon id in prod and test: 30% off any plan, forever.
    couponCode: { monthly: 'herQBNeI', yearly: 'herQBNeI' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 30, yearly: 30 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/peachjar-logo.png',
      logoHeight: 80,
      logoWidth: 200,
    },
  },
  atxmom: {
    name: 'Austin Moms',
    showOfferBadge: true,
    couponCode: { monthly: 'MNok7s9b', yearly: 'MNok7s9b' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/Austin-Moms-Logo.png',
      logoHeight: 80,
      logoWidth: 80,
      heroClassName: 'md:scale-110',
    },
  },
  elevkalenderen: {
    name: 'Elevkalenderen',
    showOfferBadge: true,
    // No yearly coupon during the exam campaign, so yearly gets the standard exam offer (1 month free).
    couponCode: { monthly: '3QDJatG1', yearly: '' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 10, yearly: 0 },
    language: AppLanguage.NO,
    logo: {
      logoType: 'awsImage',
      src: 'partners/elevkalender-logo-v2.png',
      logoHeight: 80,
      logoWidth: 120,
    },
  },
  cfa: {
    name: 'Chick-Fil-A employees',
    showOfferBadge: true,
    couponCode: appConfig.isProdEnvironment
      ? { monthly: 'AgA9NOFQ', yearly: 'AgA9NOFQ' }
      : { monthly: 'gnYos6re', yearly: 'gnYos6re' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/chick-fil-a-logo.svg',
      logoHeight: 80,
      logoWidth: 120,
    },
  },
  cellcom: {
    name: 'Cellcom',
    showOfferBadge: true,
    couponCode: { monthly: 'TeQWn2gb', yearly: 'TeQWn2gb' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/cellcom-logo.svg',
      logoHeight: 80,
      logoWidth: 90,
    },
  },
  amazon: {
    name: 'Amazon',
    showOfferBadge: true,
    couponCode: { monthly: 'pyHL0H8B', yearly: 'pyHL0H8B' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/amazon-white.png',
      logoHeight: 80,
      logoWidth: 120,
      heroClassName: 'translate-y-1',
    },
  },
  stackbyme: {
    name: 'Stack by me',
    showOfferBadge: true,
    couponCode: { monthly: 'ynI5TLF8', yearly: 'ynI5TLF8' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    logo: {
      logoType: 'awsImage',
      src: 'partners/stack-logo.png',
      logoHeight: 120,
      logoWidth: 200,
    },
  },
  dnb: {
    'ung-pluss': {
      name: 'DNB Ung Pluss',
      usesLandingHero: true,
      couponCode: { monthly: '', yearly: '' },
      offerType: OFFER_TYPE.PROCENTAGE_OFF,
      procentageOff: { monthly: 0, yearly: 0 },
      language: AppLanguage.NO,
      isLongPage: true,
      customPriceData: {
        monthlyPriceId: appConfig.isProdEnvironment
          ? 'price_1UM2mbJhgnSRU9kun2zrwvRk'
          : 'price_1Rh8c8JhgnSRU9kuX7Oj9BmJ',
        yearlyPriceId: appConfig.isProdEnvironment
          ? 'price_1UM2pCJhgnSRU9kuHTlHDwUZ'
          : 'price_1Rh8eLJhgnSRU9kuHHZMkKvO',
      },
      logo: {
        logoType: 'awsImage',
        src: 'partners/dnb-logo.svg',
        logoHeight: 40,
        logoWidth: 60,
        imageClassName: 'h-5 md:h-12 mt-1.5 md:mt-0 w-auto object-contain',
      },
      customTextKey: {
        offerTextKey: 'customText.dnbOffer',
      },
    },
    'saga-private-banking': {
      name: 'DNB Saga Private Banking',
      usesLandingHero: true,
      couponCode: { monthly: '', yearly: '' },
      offerType: OFFER_TYPE.PROCENTAGE_OFF,
      procentageOff: { monthly: 0, yearly: 0 },
      language: AppLanguage.NO,
      isLongPage: true,
      hasToD: true,
      customPriceData: {
        monthlyPriceId: appConfig.isProdEnvironment
          ? 'price_1UM2mbJhgnSRU9kun2zrwvRk'
          : 'price_1Rh8c8JhgnSRU9kuX7Oj9BmJ',
        yearlyPriceId: appConfig.isProdEnvironment
          ? 'price_1UM2pCJhgnSRU9kuHTlHDwUZ'
          : 'price_1Rh8eLJhgnSRU9kuHHZMkKvO',
      },
      logo: {
        logoType: 'awsImage',
        src: 'partners/dnb-logo.svg',
        logoHeight: 40,
        logoWidth: 60,
        imageClassName: 'h-5 md:h-12 mt-1.5 md:mt-0 w-auto object-contain',
      },
      customTextKey: {
        offerTextKey: 'customText.dnbOffer',
      },
    },
  },
} as const satisfies PartnerRecords
